<script lang="ts" setup>
import { useAppConfig } from 'nuxt/app';
import { AppTypography } from '~/components/base/';

const {
  password = '',
  label = 'Password must contain',

  requireLength = true,
  requireCase = true,
  requireNumber = true,
  requireSpecial = true,
} = defineProps<{
  password?: string;
  label?: string;

  requireLength?: boolean;
  requireCase?: boolean;
  requireNumber?: boolean;
  requireSpecial?: boolean;
}>();

const appConfig = useAppConfig();

const checkers = computed(
  (): {
    id: string;
    text: string;
    regex: RegExp;
  }[] => {
    const arr = [];

    if (requireLength !== false) {
      const minLength = appConfig.passwordCriteria.minLength;
      arr.push({
        id: 'validate-length',
        text: `At least ${minLength} characters`,
        regex: new RegExp(`.{${minLength},}`),
      });
    }
    if (requireCase !== false) {
      const minUppercase = appConfig.passwordCriteria.minUppercase;
      arr.push({
        id: 'validate-case',
        text: `Must include at least ${minUppercase} uppercase and lowercase letter${minUppercase > 1 ? 's' : ''}.`,
        regex: new RegExp(
          `(?=(?:.*[A-Z]){${minUppercase},})(?=(?:.*[a-z]){${minUppercase},})`,
        ),
      });
    }
    if (requireNumber !== false) {
      const minNumbers = appConfig.passwordCriteria.minNumbers;
      arr.push({
        id: 'validate-number',
        text: `Must contain at least ${minNumbers} numeric character${minNumbers > 1 ? 's' : ''} (0-9).`,
        regex: new RegExp(`(?=(?:.*[0-9]){${minNumbers},})`),
      });
    }
    if (requireSpecial !== false) {
      const minSpecialChars = appConfig.passwordCriteria.minSpecialCharacters;
      arr.push({
        id: 'validate-special',
        text: `Must contain at least ${minSpecialChars} special character${minSpecialChars > 1 ? 's' : ''} (e.g., !@#$%^&*).`,
        regex: new RegExp(`(?=(?:.*[^A-Za-z0-9]){${minSpecialChars},})`),
      });
    }

    return arr;
  },
);

const validateChecker = (regex: RegExp): boolean => {
  return regex.test(password);
};
</script>

<template>
  <div class="password-strength text-left">
    <AppTypography
      type="caption-bold"
      class="text-color-tertiary mb-3 uppercase"
      >{{ label }}</AppTypography
    >
    <ul class="password-strength__listmt-2 list-inside list-none">
      <li
        v-for="(checker, index) in checkers"
        :key="index"
        :class="{
          'password-strength__list__item--valid': validateChecker(
            checker.regex,
          ),
        }"
        :data-testid="`password-strength-checker-${checker.id}`"
        class="password-strength__list__item flex items-center"
      >
        <i class="pi pi-check semantic-color-success" aria-hidden="true" />
        <i class="pi pi-circle-fill" aria-hidden="true" />
        <AppTypography type="footnote" class="ml-4">{{
          checker.text
        }}</AppTypography>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.password-strength {
  &__list {
    &__item {
      &:not(:last-child) {
        margin-bottom: 0.5rem;
      }

      i.pi-check {
        display: none;
        font-size: 12px;
      }

      i.pi-circle-fill {
        display: block;
        font-size: 8px;
      }

      &--valid {
        i.pi-check {
          display: block;
        }

        i.pi-circle-fill {
          display: none;
        }

        p {
          color: $semantic-color-success;
        }
      }
    }
  }
}
</style>
